import React, { useState, useEffect, useRef } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { SearchRequest } from "./brandguard.metrics_pb"
import { Bar, Doughnut } from "charts"
import * as icons from "icons"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { getDaysArray, toDayKey, capitalize, INFISO, NEGINFISO } from "./dates"

const eventTypes = ["image", "text", "document", "video"] as const

const typeColors: Record<string, { base: string; alpha: string }> = {
	image: layouts.theme.colors.charts.blue,
	text: layouts.theme.colors.charts.green,
	document: layouts.theme.colors.charts.yellow,
	video: layouts.theme.colors.charts.violet,
}

interface typeAggregate {
	totalScored: number
	approvedCount: number
	rejectedCount: number
	reviewCount: number
	autoApprovedCount: number
	byDay: { [date: string]: number }
}

interface parsed {
	labels: string[]
	types: Record<string, typeAggregate>
}

function emptyAggregate(): typeAggregate {
	return {
		totalScored: 0,
		approvedCount: 0,
		rejectedCount: 0,
		reviewCount: 0,
		autoApprovedCount: 0,
		byDay: {},
	}
}

function parsedInitial(): parsed {
	const types: Record<string, typeAggregate> = {}
	eventTypes.forEach((t) => {
		types[t] = emptyAggregate()
	})
	return { labels: [], types }
}

function parse(r: api.AssetTypeMetricSearchResponse, dr: metasearch.DateRange, uid: string = uuid.NIL): parsed {
	const data = parsedInitial()

	getDaysArray(new Date(dr.oldest), new Date(dr.newest)).forEach((i) => {
		const day = toDayKey(i)
		data.labels.push(day)
		eventTypes.forEach((t) => {
			data.types[t].byDay[day] = 0
		})
	})

	;(r.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		const day = toDayKey(new Date(e.range!.oldest))
		const t = e.event_type
		if (!data.types[t]) return

		const total = Number(BigInt(e.total_scored || 0))
		const approved = Number(BigInt(e.approved_count || 0))
		const rejected = Number(BigInt(e.rejected_count || 0))
		const review = Number(BigInt(e.review_count || 0))
		const autoApproved = Number(BigInt(e.auto_approved_count || 0))

		data.types[t].totalScored += total
		data.types[t].approvedCount += approved
		data.types[t].rejectedCount += rejected
		data.types[t].reviewCount += review
		data.types[t].autoApprovedCount += autoApproved
		data.types[t].byDay[day] = (data.types[t].byDay[day] || 0) + total
	})

	return data
}

function approvalRate(agg: typeAggregate): string {
	if (agg.totalScored === 0) return "0%"
	const rate = ((agg.approvedCount + agg.autoApprovedCount) / agg.totalScored) * 100
	return `${rate.toFixed(1)}%`
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial())
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [response, setResponse] = useState({} as api.AssetTypeMetricSearchResponse)
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		setChartData(parse(response, req.range!, user.key))
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_asset_types(req, bearertoken))
			.then((resp) => {
				setResponse(resp)
				setChartData(parse(resp, req.range!, currentUserRef.current.key))
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req])

	const stackedOptions = {
		maintainAspectRatio: true,
		scales: {
			x: {
				stacked: true,
				ticks: {
					color: layouts.theme.colors.grey.light30,
				},
				grid: {
					display: false,
				},
			},
			y: {
				stacked: true,
				type: "linear" as const,
				display: true,
				position: "left" as const,
				min: 0,
				ticks: {
					color: layouts.theme.colors.grey.light30,
					maxTicksLimit: 5,
				},
				title: {
					display: true,
					text: "Scored Volume",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
					padding: 0,
					align: "center" as const,
				},
				grid: {
					display: false,
				},
			},
		},
		layout: { padding: 20 },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: {
					usePointStyle: true,
					boxWidth: 50,
					font: { size: 10, weight: "bold" as const },
				},
			},
		},
	}

	const stackedData = {
		labels: chartData.labels,
		datasets: eventTypes.map((t) => ({
			label: capitalize(t),
			data: chartData.labels.map((day) => chartData.types[t].byDay[day] || 0),
			backgroundColor: typeColors[t].alpha,
			borderColor: typeColors[t].base,
			borderWidth: 1,
		})),
	}

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<layouts.containers.box className={classnames("primary", metricsLayouts.hover)} height="512px" maxHeight="512px">
						<Bar options={stackedOptions} data={stackedData} width={100} />
					</layouts.containers.box>
					<layouts.containers.flex justifyContent="center" flexWrap="wrap">
						{eventTypes.map((t) => {
							const agg = chartData.types[t]
							const doughnutData = {
								labels: ["Approved", "Auto Approved", "Rejected", "In Review"],
								datasets: [
									{
										data: [agg.approvedCount, agg.autoApprovedCount, agg.rejectedCount, agg.reviewCount],
										backgroundColor: [
											layouts.theme.colors.charts.blue.alpha,
											layouts.theme.colors.charts.green.alpha,
											layouts.theme.colors.charts.yellow.alpha,
											layouts.theme.colors.charts.violet.alpha,
										],
										borderColor: [
											layouts.theme.colors.charts.blue.base,
											layouts.theme.colors.charts.green.base,
											layouts.theme.colors.charts.yellow.base,
											layouts.theme.colors.charts.violet.base,
										],
										borderWidth: 1,
									},
								],
							}
							const doughnutOptions = {
								responsive: true,
								maintainAspectRatio: true,
								cutout: "60%",
								plugins: {
									legend: { display: false },
								},
							}

							return (
								<metricsLayouts.Card
									key={t}
									flex="1"
									maxWidth="49%"
									minHeight="280px"
									maxHeight="280px"
									padding="20px"
									mr="1px"
									textAlign="center"
									flexDirection="column"
								>
									<metricsLayouts.Header mb="15px">{capitalize(t)}</metricsLayouts.Header>
									<layouts.containers.flex>
										<layouts.containers.flex flex="1" justifyContent="center" alignItems="center" maxWidth="120px" maxHeight="120px">
											<Doughnut data={doughnutData} options={doughnutOptions} />
										</layouts.containers.flex>
										<layouts.containers.flex flex="1" flexDirection="column" ml="15px" justifyContent="center">
											<layouts.containers.flex alignItems="center" mb="5px">
												<layouts.containers.flex pr="10px">
													<icons.ImpressionsEye
														fill={typeColors[t].base}
														width="24px"
														height="24px"
														verticalAlign="middle"
													/>
												</layouts.containers.flex>
												<layouts.containers.flex fontSize="24px" fontWeight="600">
													{approvalRate(agg)}
												</layouts.containers.flex>
											</layouts.containers.flex>
											<layouts.containers.span fontSize="11px" color={layouts.theme.colors.grey.dark10}>
												Approval Rate
											</layouts.containers.span>
										</layouts.containers.flex>
									</layouts.containers.flex>
									<layouts.containers.flex flexDirection="column" mt="15px">
										<layouts.containers.flex justifyContent="space-between" mb="5px">
											<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
												Total Scored
											</layouts.containers.span>
											<layouts.containers.span fontWeight="600" fontSize="12px">
												{agg.totalScored}
											</layouts.containers.span>
										</layouts.containers.flex>
										<layouts.containers.flex justifyContent="space-between" mb="5px">
											<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
												Approved
											</layouts.containers.span>
											<layouts.containers.span fontWeight="600" fontSize="12px">
												{agg.approvedCount}
											</layouts.containers.span>
										</layouts.containers.flex>
										<layouts.containers.flex justifyContent="space-between" mb="5px">
											<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
												Auto Approved
											</layouts.containers.span>
											<layouts.containers.span fontWeight="600" fontSize="12px">
												{agg.autoApprovedCount}
											</layouts.containers.span>
										</layouts.containers.flex>
										<layouts.containers.flex justifyContent="space-between" mb="5px">
											<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
												Rejected
											</layouts.containers.span>
											<layouts.containers.span fontWeight="600" fontSize="12px">
												{agg.rejectedCount}
											</layouts.containers.span>
										</layouts.containers.flex>
										<layouts.containers.flex justifyContent="space-between">
											<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
												In Review
											</layouts.containers.span>
											<layouts.containers.span fontWeight="600" fontSize="12px">
												{agg.reviewCount}
											</layouts.containers.span>
										</layouts.containers.flex>
									</layouts.containers.flex>
								</metricsLayouts.Card>
							)
						})}
					</layouts.containers.flex>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
